import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDate } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { usePersonalExpensesStore } from "@/stores/personal-expenses-store"
import type { UpdatePersonalExpensePayload } from "@/types/personal-expenses"

import { API_BASE_URL } from "@/lib/axios"
import {
  AlertCircle,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Trash2,
  Save,
  User,
  Calendar,
} from "lucide-react"

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

export default function PersonalExpenseDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentExpense,
    isLoading,
    isUpdating,
    isDeleting,
    error,
    fetchDetail,
    update,
    delete: deleteExpense,
    clearCurrentExpense,
  } = usePersonalExpensesStore()

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<UpdatePersonalExpensePayload>({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (id) {
      fetchDetail(parseInt(id))
    }
    return () => {
      clearCurrentExpense()
    }
  }, [id, fetchDetail, clearCurrentExpense])

  useEffect(() => {
    if (currentExpense) {
      setFormData({
        amount: currentExpense.amount,
        revenue_type: currentExpense.revenue_type,
        description: currentExpense.description,
      })
    }
  }, [currentExpense])

  const handleSave = async () => {
    if (!id) return

    const success = await update(parseInt(id), formData)
    if (success) {
      setIsEditing(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    const success = await deleteExpense(parseInt(id))
    if (success) {
      navigate("/personal-expenses")
    }
  }

  const handleCancel = () => {
    if (currentExpense) {
      setFormData({
        amount: currentExpense.amount,
        revenue_type: currentExpense.revenue_type,
        description: currentExpense.description,
      })
    }
    setIsEditing(false)
  }

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(num)
  }

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString("en-US", {
  //     year: "numeric",
  //     month: "long",
  //     day: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //   })
  // }

  const getAvatarUrl = (avatar: string | null) => {
    if (!avatar) return ""
    if (avatar.startsWith("http")) return avatar
    const baseUrl = API_BASE_URL.replace(/\/api$/, "")
    return `${baseUrl}${avatar}`
  }

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/personal-expenses">{t('expenses.personalExpenses')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {currentExpense ? t('expenses.personalExpenseNumber', { id: currentExpense.id }) : t('common.loading')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className="mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/personal-expenses">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('expenses.backToPersonalExpenses')}
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : currentExpense ? (
          <div className="grid gap-4 md:grid-cols-3 items-start">
            {/* Main Details Card */}
            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  {currentExpense.revenue_type === "income" ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                      <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                      <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                  <div>
                    <CardTitle>{t('expenses.personalExpenseNumber', { id: currentExpense.id })}</CardTitle>
                    <CardDescription>
                      {t('expenses.created')} {formatDate(currentExpense.created_at)}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        disabled={isUpdating}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={isUpdating}
                      >
                        <Save className="mr-2 h-4 w-4" />
                        {isUpdating ? t('common.saving') : t('common.save')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="amount">{t('expenses.amount')}</Label>
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          value={formData.amount || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, amount: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="revenue_type">{t('expenses.revenueType')}</Label>
                        <Select
                          value={formData.revenue_type}
                          onValueChange={(value: "income" | "outcome") =>
                            setFormData({ ...formData, revenue_type: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="income">{t('expenses.income')}</SelectItem>
                            <SelectItem value="outcome">{t('expenses.expense')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">{t('expenses.description')}</Label>
                      <Textarea
                        id="description"
                        value={formData.description || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, description: e.target.value })
                        }
                        rows={4}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('expenses.amount')}</p>
                        <p
                          className={`mt-1 text-2xl font-bold ${currentExpense.revenue_type === "income" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                        >
                          {currentExpense.revenue_type === "income" ? "+" : "-"}
                          {formatCurrency(currentExpense.amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('expenses.revenueType')}
                        </p>
                        <p className="mt-1 text-lg font-medium capitalize">
                          {currentExpense.revenue_type === 'income' ? t('expenses.income') : t('expenses.expense')}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('expenses.description')}</p>
                      <p className="mt-2 whitespace-pre-wrap leading-relaxed">
                        {currentExpense.description || (
                          <span className="text-muted-foreground italic">
                            {t('expenses.noDescription')}
                          </span>
                        )}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Sidebar Info */}
            <div className="space-y-4">
              {/* Admin Card */}
              {currentExpense.admin && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      {t('expenses.createdBy')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage
                          src={getAvatarUrl(currentExpense.admin.avatar)}
                          alt={currentExpense.admin.first_name}
                        />
                        <AvatarFallback>
                          {currentExpense.admin.first_name[0]}
                          {currentExpense.admin.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {currentExpense.admin.first_name}{" "}
                          {currentExpense.admin.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {currentExpense.admin.email}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Timestamps Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium">
                    <Calendar className="h-4 w-4" />
                    {t('expenses.timestamps')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('expenses.created')}</p>
                    <p className="text-sm">
                      {formatDate(currentExpense.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('expenses.lastUpdated')}</p>
                    <p className="text-sm">
                      {formatDate(currentExpense.updated_at)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              {t('expenses.expenseNotFound')}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>{t('expenses.deletePersonalExpense')}</CardTitle>
                <CardDescription>
                  {t('expenses.deletePersonalConfirmation')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('common.deleting') : t('common.delete')}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  )
}
